import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    type ColumnVisibilityState,
    type ExpandedState,
    type FilterFn,
    functionalUpdate,
    type PaginationState,
    type RowData,
    type SortingState,
    useTable,
} from '@tanstack/react-table';

import { withEmptyValuesUndefined } from './emptyValues';
import { type DataTableFeatures, type DataTableInstance, dataTableFeatures } from './features';
import type { DataTableProps } from './types';

/** Matches a row when any filterable cell's text contains the search, case-insensitively. */
function globalFilterFn<T extends RowData>(): FilterFn<DataTableFeatures, T> {
    return (row, _columnId, filterValue) => {
        const search = String(filterValue || '')
            .toLowerCase()
            .trim();
        if (!search) return true;

        return row.getAllCells().some((cell) => {
            if (cell.column.columnDef.enableGlobalFilter === false) return false;
            const searchText = cell.column.columnDef.meta?.searchText;
            if (searchText) return searchText(row).toLowerCase().includes(search);
            const value = cell.getValue();
            if (value === null || value === undefined) return false;
            return String(value).toLowerCase().includes(search);
        });
    };
}

/**
 * The tanstack table behind DataTable: sorting, the global filter, pagination, column
 * visibility and expansion, each controlled by the caller when it passes the state.
 */
export function useDataTable<T extends RowData>(props: DataTableProps<T>): DataTableInstance<T> {
    const {
        data,
        columns,
        getRowId,
        enableSorting = true,
        initialSort,
        filter,
        enablePagination = false,
        pageSize = 15,
        columnVisibility,
        onColumnVisibilityChange,
        getSubRows,
        renderExpanded,
        getRowCanExpand,
        expanded,
        onExpandedChange,
    } = props;

    const [sorting, setSorting] = useState<SortingState>(initialSort || []);
    const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize });
    const [internalVisibility, setInternalVisibility] = useState<ColumnVisibilityState>(
        columnVisibility || {}
    );
    const [internalExpanded, setInternalExpanded] = useState<ExpandedState>({});

    useEffect(() => {
        if (enablePagination) {
            setPagination((prev) => ({ ...prev, pageSize }));
        }
    }, [pageSize, enablePagination]);

    useEffect(() => {
        if (columnVisibility !== undefined) {
            setInternalVisibility(columnVisibility);
        }
    }, [columnVisibility]);

    // tanstack caches each row's column values until `data` changes, so a value an accessor
    // translates would keep the old language. A copy per language rebuilds the rows instead.
    // Test mocks of react-i18next often leave `i18n` out, hence the optional read.
    const language = useTranslation().i18n?.language;
    const rows = useMemo(() => (language === undefined ? data : data.slice()), [data, language]);
    // New rows make tanstack go back to the first page and collapse expanded rows, as it should
    // for new data. A copy made only for a language switch is the same data, so on that render
    // the reader stays where they were.
    const shownLanguage = useRef(language);
    const languageSwitched = shownLanguage.current !== language;
    useEffect(() => {
        shownLanguage.current = language;
    }, [language]);

    const tableColumns = useMemo(() => withEmptyValuesUndefined(columns), [columns]);

    const effectiveVisibility = columnVisibility ?? internalVisibility;
    const effectiveExpanded = expanded ?? internalExpanded;
    const canExpand = getSubRows !== undefined || renderExpanded !== undefined;

    return useTable<DataTableFeatures, T>({
        features: dataTableFeatures,
        data: rows,
        columns: tableColumns,
        // An empty cell sorts after every value whichever way the column sorts. A column can
        // still set its own `sortUndefined`.
        defaultColumn: { sortUndefined: 'last' },
        getRowId,
        state: {
            sorting,
            globalFilter: filter,
            pagination,
            columnVisibility: effectiveVisibility,
            expanded: effectiveExpanded,
        },
        enableSorting,
        enableFilters: true,
        enableGlobalFilter: true,
        // The table already honours each column's `enableGlobalFilter`; this only drops the
        // default check that a column's first value is a string or number.
        getColumnCanGlobalFilter: () => true,
        globalFilterFn: globalFilterFn<T>(),
        ...(languageSwitched ? { autoResetPageIndex: false, autoResetExpanded: false } : {}),
        onSortingChange: setSorting,
        onPaginationChange: setPagination,
        onColumnVisibilityChange: (updater) => {
            const next = functionalUpdate(updater, effectiveVisibility);
            if (columnVisibility === undefined) setInternalVisibility(next);
            onColumnVisibilityChange?.(next);
        },
        onExpandedChange: (updater) => {
            const next = functionalUpdate(updater, effectiveExpanded);
            if (expanded === undefined) setInternalExpanded(next);
            onExpandedChange?.(next);
        },
        getSubRows,
        getRowCanExpand,
        // Every row model is registered once in the features; these switch off the ones a
        // table does not use, so its rows come through whole and unexpanded.
        manualExpanding: !canExpand,
        manualPagination: !enablePagination,
    });
}
